import * as React from 'react';
import CallbackHelpers from '../../shared-common/helpers/CallbackHelpers';

export interface MercuryCacheProps {
  executeAllPendingQueries: () => Promise<void>;
  flushCache: () => void;
  hasPendingQueries: boolean;
  cancelPendingQueries: () => void;

  /**
   * True once the backend has refused a query batch as forbidden.
   *
   * Individual widgets already render a refused query as unavailable, but they cannot tell "no data" from
   * "not allowed to see this data". A page that is reached by a scoped link - a report opened from a magic
   * link, say - uses this to say so once rather than leaving every widget silently blank.
   */
  isAccessDenied: boolean;

  /**
   * Why a query batch could not be answered, when the reason was not a refusal: 'analytics_not_yet_available'
   * when the dataset has not been preprocessed, 'query_not_supported' when the fast path cannot serve the
   * query shape for a caller whose access is narrowed, or 'unknown'. Null while nothing has failed this way.
   *
   * Separate from <see cref="isAccessDenied" /> because neither is a permissions problem, and telling someone
   * they lack access to a report they hold is worse than saying nothing.
   */
  unavailableReason: string | null;
}

export const MercuryCache = React.createContext<MercuryCacheProps>({
  executeAllPendingQueries: CallbackHelpers.AsyncNoOp,
  flushCache: CallbackHelpers.ThrowNotImplemented,
  hasPendingQueries: false,
  cancelPendingQueries: CallbackHelpers.NoOp,
  isAccessDenied: false,
  unavailableReason: null
});
